import { ParsedDescopeTheme } from 'modules/theme/theme';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface IDescopeThemeFontLoaderProps {
	theme: ParsedDescopeTheme;
	setThemeLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}

const DescopeThemeFontLoader: React.FC<IDescopeThemeFontLoaderProps> = ({
	theme,
	setThemeLoaded
}) => {
	const [loadedFonts, setLoadedFonts] = useState<Record<string, boolean>>({});

	const fontUrlsToLoad = useMemo(() => {
		/* istanbul ignore next */
		if (!theme) {
			return null;
		}
		const seen: Record<string, boolean> = {};

		return [
			theme?.light?.globalsVars['--descope-fonts-font1-url'],
			theme?.light?.globalsVars['--descope-fonts-font2-url'],
			theme?.dark?.globalsVars['--descope-fonts-font1-url'],
			theme?.dark?.globalsVars['--descope-fonts-font2-url']
		]
			.filter(Boolean)
			.filter((url) => {
				/* istanbul ignore next */
				if (seen[url!]) {
					return false;
				}
				seen[url!] = true;
				return true;
			}) as string[];
	}, [theme]);

	const markFontResolved = useCallback((url: string) => {
		setLoadedFonts((prev) => (prev[url] ? prev : { ...prev, [url]: true }));
	}, []);

	useEffect(() => {
		setLoadedFonts({});
	}, [fontUrlsToLoad]);

	useEffect(() => {
		/* istanbul ignore next */
		if (!Array.isArray(fontUrlsToLoad)) {
			return;
		}

		if (fontUrlsToLoad.length === 0) {
			setThemeLoaded(true);
			return;
		}

		setThemeLoaded(fontUrlsToLoad.every((url) => loadedFonts[url]));
	}, [fontUrlsToLoad, loadedFonts, setThemeLoaded]);

	if (!Array.isArray(fontUrlsToLoad) || fontUrlsToLoad.length === 0) {
		return null;
	}

	return (
		<>
			{fontUrlsToLoad.map((url) => (
				<link
					key={url}
					ref={(el) => {
						/* istanbul ignore next */
						if (el?.sheet) {
							markFontResolved(url);
						}
					}}
					href={url.replace(/^url\(/, '').replace(/\)$/, '')}
					data-testid="font-load-script"
					rel="stylesheet"
					type="text/css"
					onLoad={() => markFontResolved(url)}
					onError={() => markFontResolved(url)}
				/>
			))}
		</>
	);
};

export default DescopeThemeFontLoader;
